import React from 'react';

import { Navigate } from 'react-router';

import { appRoutes } from '@clearverify/constants';
import type { SimpleObject } from '@clearverify/types';

import { SignUpMethod, useGetPeoBasicInfoQuery } from 'src/api';
import { IS_PRODUCTION_BUILD } from 'src/config';
import { SignupConsumerFormStepId } from 'src/constants/auth';
import { usePeoParamId } from 'src/hooks/peo';
import { useCurrentUserStatesList } from 'src/hooks/states';

import {
  AuthSignupConsumerContext,
  AuthSignupConsumerContextValue,
  FormStepperConsumerState,
} from './AuthSignupConsumerContext';

export const AuthSignupConsumerPeoProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const peoId = usePeoParamId();
  const { data, loading, error } = useGetPeoBasicInfoQuery({
    variables: {
      id: String(peoId),
    },
    skip: !peoId,
  });

  const [formData, setFormData] = React.useState<SimpleObject>({});
  const [employeeSignupToken, setEmployeeSignupToken] = React.useState<string | null>(null);

  const homeAddress = formData.homeAddress as
    | {
        state: string | undefined | null;
      }
    | undefined;

  const { isRequestAutoApproveStatusInactive } = useCurrentUserStatesList({
    state: homeAddress?.state,
  });

  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);

  const consumerFormFlow = React.useMemo(() => {
    return [
      data?.getPeoBasicInfo?.signUpMethod === SignUpMethod.Ssn
        ? SignupConsumerFormStepId.ssn
        : SignupConsumerFormStepId.pin,
      SignupConsumerFormStepId.consumerBasicInfo,
      SignupConsumerFormStepId.consumerAttestations,
      ...(isRequestAutoApproveStatusInactive
        ? []
        : [SignupConsumerFormStepId.consumerVerificationPreference]),
      SignupConsumerFormStepId.consumerReviewData,
    ];
  }, [data?.getPeoBasicInfo?.signUpMethod, isRequestAutoApproveStatusInactive]);

  const formStepperState = React.useMemo((): FormStepperConsumerState => {
    const nextStepId = consumerFormFlow[currentStepIndex + 1];

    return {
      previousStepId: consumerFormFlow[currentStepIndex - 1],
      currentStepId: consumerFormFlow[currentStepIndex] as string,
      nextStepId,
      isNextStepAvailable: Boolean(nextStepId),
      isLastStep: currentStepIndex === consumerFormFlow.length - 1,
    };
  }, [currentStepIndex, consumerFormFlow]);

  const goToNextStep = React.useCallback(() => {
    if (formStepperState.nextStepId) {
      setCurrentStepIndex(currentIndex => currentIndex + 1);
    }
  }, [formStepperState.nextStepId]);

  const goToPreviousStep = React.useCallback(() => {
    if (formStepperState.previousStepId) {
      setCurrentStepIndex(currentIndex => currentIndex - 1);
    }
  }, [formStepperState.previousStepId]);

  const updateFormData = React.useCallback((formValues: SimpleObject) => {
    setFormData(currentFormData => {
      return { ...currentFormData, ...formValues };
    });
  }, []);

  const contextValue: AuthSignupConsumerContextValue = React.useMemo(() => {
    return {
      peoInfo: data?.getPeoBasicInfo,
      loading,
      formData,
      setFormData,
      updateFormData,
      employeeSignupToken,
      setEmployeeSignupToken,
      formStepperState,
      goToNextStep,
      goToPreviousStep,
    };
  }, [
    data?.getPeoBasicInfo,
    formData,
    formStepperState,
    employeeSignupToken,
    goToNextStep,
    goToPreviousStep,
    loading,
    updateFormData,
  ]);

  if (!peoId || (IS_PRODUCTION_BUILD && peoId && !loading && error)) {
    return <Navigate to={appRoutes.auth.index} />;
  }

  return <AuthSignupConsumerContext value={contextValue}>{children}</AuthSignupConsumerContext>;
};
